import { z } from "zod";

// TES-1001 (Ö-249) — "Information att uppmärksamma": every free text the
// patient wrote in a medical form, shown to the doctor as she wrote it.
//
// Emelie, TES-996, 2026-09-29:
//   "all patientskriven fritext från F-01, F-02/F-02B, F-07, vecko- och månadsincheckningar samt andra medicinska formulär ska visas för Kirill som Information att uppmärksamma."
// For each text: the full text unedited, the question it answers, the main
// answer that opened the follow-up (when there is one), a readable form
// name, the submission date and time, and a link to the source answer.
//   "En sektionsrubrik får aldrig ersätta patientens faktiska text."
//
// This is visibility, not severity. An item here is NOT a risk flag: only an
// answer matching the versioned risk code list is a "Medicinsk riskflagga",
// and that list is a separate question (TES-1001, part 1).

const answerLineSchema = z.object({
  fieldKey: z.string(),
  // The question as the form version asked it; null when that version has
  // no such field (the key is then the only name there is).
  label: z.string().nullable(),
  // As the doctor reads it: option labels resolved, free text verbatim.
  value: z.string(),
});
export type AttentionAnswerLine = z.infer<typeof answerLineSchema>;

export const attentionTextSchema = z.object({
  submissionId: z.string(),
  // The stored template key; the admin turns it into the readable form name
  // (formDisplayName), which for `intake` depends on the patient's journey.
  templateKey: z.string(),
  templateVersion: z.number().int(),
  submittedAt: z.string().datetime(),
  fieldKey: z.string(),
  // The question the text answers, as that form version asked it.
  question: z.string(),
  // Her words, byte for byte: never trimmed, summarised or re-worded.
  text: z.string(),
  // The answer that opened this follow-up question, when the field is a
  // follow-up (showWhen.parentKey). Null for a question always shown.
  parent: answerLineSchema.nullable(),
});
export type AttentionText = z.infer<typeof attentionTextSchema>;

// The whole submission behind one or more texts: the target of the link to
// the source answer, so the doctor can read the text in the form it came
// from without leaving the page.
export const attentionSourceSchema = z.object({
  submissionId: z.string(),
  templateKey: z.string(),
  templateVersion: z.number().int(),
  submittedAt: z.string().datetime(),
  answers: z.array(answerLineSchema),
});
export type AttentionSource = z.infer<typeof attentionSourceSchema>;

export const patientAttentionTextsSchema = z.object({
  // Newest submission first; within one, in the form's own question order.
  items: z.array(attentionTextSchema),
  // One per submission that has at least one item.
  sources: z.array(attentionSourceSchema),
});
export type PatientAttentionTexts = z.infer<typeof patientAttentionTextsSchema>;

